import { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import DrmTable from "../tmt/DrmTable";
import TableActionButtons from "../tmt/TableActionButtons";
import { DRM_COLUMN } from "../../constData/columnDrmt";
import { pageSize } from "../../helpers";
import { getCoupaFilesDeleteDetails } from "../../redux/reducers/drmCoupaFIleDeleteDetailsSlice";
import { coupaFilterPayload } from "../../constData/filterRequestPayloadCoupa";
import { setCoupaTableFilter, resetCoupaColumnTableFilters } from "../../redux/reducers/drmCoupaTableFilterSlice";
import { useDrmFilterUrlSync } from "../../customHook/useDrmFilterUrlSync";
import CustomButton from "../common/CustomButton";
import CustomSnackbar from "../common/CustomSnackbar";
import DialogConformationMsg from "../tmt/DialogConformationMsg";
import { deleteCoupaFilesStart, resetDeleteCoupaFilesState } from "../../redux/reducers/drmCoupaDeleteSlice";
import { updateCoupaFilesStart, resetUpdateCoupaFilesState } from "../../redux/reducers/drmCoupaUpdateSlice";
import { expireCoupaFilesStart, resetExpireCoupaFilesState } from "../../redux/reducers/drmCoupaExpireSlice";
import "./CoupaFIleDeleteDetails.sass";

const INITIAL_TABLE_STATE = {
  originalData: [],
  headers: [],
  toggleProps: null,
  density: "small",
  currentPage: 1,
  updatedData: [],
  selectedRows: [],
  modifiedRows: {},
  newAddedRows: [],
};

const COUPA_SPECIFIC_COLUMNS = [
  "fileNames",
  "fileCategory",
  "createdAt",
  "countOfProcessableRecords",
  "countOfDeletableRecords",
  "countOfRateExpirationRecords"
];

const ActionButton = ({ datatestid, onClick, disabled, children, ...props }) => (
  <CustomButton
    datatestid={datatestid}
    size="small"
    variant="outlined"
    btnType="nonary"
    onClick={onClick}
    disabled={disabled}
    className="coupa-action-button"
    {...props}
  >
    {children}
  </CustomButton>
);

function CoupaFIleDeleteDetails() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { drmCoupaFileDeleteDetails: coupaDeleteRows, isLoading: isDrmCoupaFileDeleteLoading, onPage, totalPages, totalRecords } = useSelector((state) => state.drmCoupaFileDeleteDetails);

  const { coupaTableFilter } = useSelector((state) => state.drmCoupaFilterPayload);

  const { isLoading: isDeleteLoading, successMessage: deleteSuccessMessage, errorMessage: deleteErrorMessage } = useSelector((state) => state.drmCoupaDelete);
  const { isLoading: isUpdateLoading, successMessage: updateSuccessMessage, errorMessage: updateErrorMessage } = useSelector((state) => state.drmCoupaUpdate);
  const { isLoading: isExpireLoading, successMessage: expireSuccessMessage, errorMessage: expireErrorMessage } = useSelector((state) => state.drmCoupaExpire);
  
  const [tableState, setTableState] = useState(INITIAL_TABLE_STATE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null });
  const [snackbarState, setSnackbarState] = useState({ open: false, message: "", severity: "info" });
  const cloneFilters = useCallback((filters) => JSON.parse(JSON.stringify(filters ?? {})), []);

  useDrmFilterUrlSync({
    scope: "coupa",
    filterState: coupaTableFilter,
    dispatch,
    setFilterAction: setCoupaTableFilter,
    uiState: null,
    setUiState: () => {},
    initialUiState: null,
    defaultFilterState: coupaFilterPayload,
    countryOptions: [],
  });

  const requestBody = useMemo(
    () => cloneFilters(coupaTableFilter || coupaFilterPayload),
    [cloneFilters, coupaTableFilter]
  );

  const selectedCoupaIds = useMemo(() => {
    const ids = (selectedRows || [])
      .map((row) => {
        const original = row?.original || {};
        return (
          original?.id ??
          null
        );
      })
      .filter((id) => id !== null && id !== undefined);

    return Array.from(new Set(ids));
  }, [selectedRows]);

  const hasSelectedRows = selectedCoupaIds.length > 0;

  const fetchCoupaDeleteDetails = useCallback(
    (page = 1) => {
      dispatch(
        getCoupaFilesDeleteDetails({
          pageSize,
          pageNum: page,
          tableName: "drmCoupaFileDeleteDetails",
          reqBody: requestBody,
        })
      );
    },
    [requestBody]
  );

  useEffect(() => {
    fetchCoupaDeleteDetails(1);
  }, [fetchCoupaDeleteDetails]);

  const tableData = useMemo(() => coupaDeleteRows || [], [coupaDeleteRows]);

  const handlePageRefresh = useCallback(
    (page) => {
      const targetPage = page ?? tableState.currentPage ?? 1;
      setTableState((prev) => ({ ...prev, currentPage: targetPage }));
      fetchCoupaDeleteDetails(targetPage);
    },
    [fetchCoupaDeleteDetails, setTableState, tableState.currentPage]
  );

  const handleClearTableFilters = useCallback(() => {
    dispatch(resetCoupaColumnTableFilters());
    handlePageRefresh(1);
  }, [dispatch, handlePageRefresh]);

  const handleSelectionChange = useCallback((rows) => {
    setSelectedRows(rows || []);
  }, []);

  const openActionDialog = useCallback((type) => {
    setActionDialog({ open: true, type });
  }, []);

  const handleActionDialogClose = useCallback(() => {
    setActionDialog({ open: false, type: null });
  }, []);

  const handleActionConfirm = useCallback(() => {
    if (!actionDialog.type) {
      return;
    }

    const ids = hasSelectedRows ? selectedCoupaIds : [];

    if (actionDialog.type === "delete") {
      dispatch(
        deleteCoupaFilesStart({
          deleteAll: !hasSelectedRows,
          coupaIds: ids,
        })
      );
    } else if (actionDialog.type === "expire") {
      dispatch(
        expireCoupaFilesStart({
          coupaIds: ids,
        })
      );
    } else {
      dispatch(
        updateCoupaFilesStart({
          updateAll: !hasSelectedRows,
          coupaIds: ids,
        })
      );
    }

    setActionDialog({ open: false, type: null });
  }, [actionDialog, hasSelectedRows, selectedCoupaIds]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  }, []);

  const handleActionSuccess = useCallback((message) => {
    setSnackbarState({ open: true, message, severity: "info" });
    handlePageRefresh(tableState.currentPage);
    setSelectedRows([]);
  }, [handlePageRefresh, tableState.currentPage]);

  useEffect(() => {
    if (deleteSuccessMessage) {
      handleActionSuccess(deleteSuccessMessage);
      dispatch(resetDeleteCoupaFilesState());
    }
  }, [deleteSuccessMessage, handleActionSuccess]);

  useEffect(() => {
    if (deleteErrorMessage) {
      setSnackbarState({ open: true, message: deleteErrorMessage, severity: "error" });
      dispatch(resetDeleteCoupaFilesState());
    }
  }, [deleteErrorMessage]);

  useEffect(() => {
    if (updateSuccessMessage) {
      handleActionSuccess(updateSuccessMessage);
      dispatch(resetUpdateCoupaFilesState());
    }
  }, [handleActionSuccess, updateSuccessMessage]);

  useEffect(() => {
    if (updateErrorMessage) {
      setSnackbarState({ open: true, message: updateErrorMessage, severity: "error" });
      dispatch(resetUpdateCoupaFilesState());
    }
  }, [updateErrorMessage]);

  useEffect(() => {
    if (expireSuccessMessage) {
      handleActionSuccess(expireSuccessMessage);
      dispatch(resetExpireCoupaFilesState());
    }
  }, [expireSuccessMessage, handleActionSuccess]);

  useEffect(() => {
    if (expireErrorMessage) {
      setSnackbarState({ open: true, message: expireErrorMessage, severity: "error" });
      dispatch(resetExpireCoupaFilesState());
    }
  }, [expireErrorMessage]);

  const columnOrder = useMemo(() => {
    return COUPA_SPECIFIC_COLUMNS.map((columnName) =>
      DRM_COLUMN.find((col) => col.Header === columnName)
    ).filter(Boolean);
  }, []);

  const deleteDialogContent = useMemo(
    () => (hasSelectedRows ? t("delete_coupa_selected_content") : t("delete_coupa_all_content")),
    [hasSelectedRows]
  );

  const updateDialogContent = useMemo(
    () => (hasSelectedRows ? t("update_coupa_selected_content") : t("update_coupa_all_content")),
    [hasSelectedRows]
  );

  const expireDialogContent = useMemo(
    () => (hasSelectedRows && t("expire_coupa_selected_content")),
    [hasSelectedRows]
  );

  const dialogTitle = actionDialog.type === "delete" ? t("delete_coupa_dialog_title") : actionDialog.type === "expire" ? t("expire_coupa_dialog_title") : t("update_coupa_dialog_title");
  const dialogContentText = actionDialog.type === "delete" ? deleteDialogContent : actionDialog.type === "expire" ? expireDialogContent : updateDialogContent;
  const dialogConfirmLabel = actionDialog.type === "delete" ? t("delete_btn") : actionDialog.type === "expire" ? t("expire_btn") : t("submit");
  const dialogLoading = actionDialog.type === "delete" ? isDeleteLoading : actionDialog.type === "expire" ? isExpireLoading : isUpdateLoading;
  const isActionLoading = isDeleteLoading || isUpdateLoading || isExpireLoading;

  return (
    <Stack spacing={2} alignItems="flex-start" width="100%" px={2}>
      <Stack alignItems="flex-start">
        <Typography variant="h4" gutterBottom>
          {t("coupa_file_delete_details")}
        </Typography>
      </Stack>
      <TableActionButtons
        tableState={tableState}
        setTableState={setTableState}
        handleRefresh={handlePageRefresh}
      />
      <DrmTable
        tableName="Coupa"
        tableData={tableData}
        columnOrder={columnOrder}
        tableState={tableState}
        editableRow={false}
        allowRowSelection
        onSelectionChange={handleSelectionChange}
        onPage={onPage}
        totalPages={totalPages}
        isLoadingDrmTable={isDrmCoupaFileDeleteLoading}
        totalRecords={totalRecords}
        setTableState={setTableState}
        handleRefresh={handlePageRefresh}
        handleClear={handleClearTableFilters}
        handleClearAllFilters={handleClearTableFilters}
      />

      <Stack direction="row" justifyContent="flex-end" spacing={1} width="100%" px={0}>
        <ActionButton
          datatestid="submit-coupa-files"
          onClick={() => openActionDialog("update")}
          disabled={isActionLoading}
          aria-label="submit-coupa-files"
        >
          {t("submit")}
        </ActionButton>
        <ActionButton
          datatestid="expire-coupa-files"
          onClick={() => openActionDialog("expire")}
          disabled={isActionLoading ||  !hasSelectedRows}
          aria-label="expire-coupa-files"
        >
          {t("expire")}
        </ActionButton>
        <ActionButton
          datatestid="delete-coupa-files"
          onClick={() => openActionDialog("delete")}
          disabled={isActionLoading}
          aria-label="delete-coupa-files"
        >
          {t("delete_btn")}
        </ActionButton>
      </Stack>

      {snackbarState.open && (
        <CustomSnackbar
          open={snackbarState.open}
          severity={snackbarState.severity}
          autoHideDuration={5000}
          datatestId="coupa-delete-feedback"
          message={snackbarState.message}
          handleClose={handleSnackbarClose}
        />
      )}

      <DialogConformationMsg
        open={actionDialog.open}
        handleClose={handleActionDialogClose}
        handleConfirm={handleActionConfirm}
        dialogTitle={dialogTitle}
        dialogContentText={dialogContentText}
        cancel={t("cancel")}
        confirm={dialogConfirmLabel}
        confirmLoading={dialogLoading}
      />
    </Stack>
  );
}

export default CoupaFIleDeleteDetails;
 